import { z } from 'zod';

/**
 * In-app notification kinds. Mirrors the GAS bell triggers (see
 * `GAS (legacy)/index.html:4901-4975`) so the new app re-establishes
 * visibility into events that the user cared about.
 *
 * Data Hub:
 *  - resource.shared_with_you   — added to `sharedWith`
 *  - resource.shared_with_area  — `sharedWithAreas` includes my area
 *  - resource.created_in_area   — new area-visible resource where my area
 *                                 is the creator's area
 *  - announcement.created       — admin published a new announcement
 *  - resource.click_milestone   — my own resource crossed a 50-click step
 *
 * Metric Hub:
 *  - metric_hub.draft_submitted — a draft entered `in_review`; reviewers notified
 *  - metric_hub.draft_decision  — a reviewer approved / rejected / requested changes / marked duplicate
 *  - metric_hub.draft_comment   — a new comment was posted on a draft I'm involved in
 *  - metric_hub.draft_mention   — somebody @-mentioned me in a comment
 *  - metric_hub.metric_comment  — a new comment was posted on an approved metric I own/watch
 *  - metric_hub.metric_mention  — somebody @-mentioned me in an approved-metric comment
 *  - metric_hub.semantic_layer_sync — the metric's dbt-repo PR transitioned
 *    (merged / closed / failed). Triggered by the GitHub webhook handler;
 *    discriminator carried in `metadata.status`.
 *  - metric_hub.training_reminder — an admin (or, later, a scheduled job)
 *    reminded the user to finish their training and earn their certificate.
 *    `metadata` carries `{ missing: string[], progressPct: number }`.
 *  - metric_hub.downstream_changed — a metric you own or depend on had a new
 *    version approved. Sent (opt-in by the reviewer) to owners + subscribers of
 *    the changed metric's blast radius. `metadata` carries `{ sourceSlug }`.
 *  - metric_hub.consumer_digest — the weekly cron's aggregated summary of the
 *    metrics you watch that changed this week. `metadata` carries
 *    `{ count, slugs: string[] }`.
 *  - metric_hub.review_reminder — a nudge to the author of a draft that has
 *    been sitting in `changes_requested`. Sent either by a reviewer clicking
 *    "Send reminder" or by the daily auto-reminder cron. `metadata` carries
 *    `{ metricSlug | semanticModelName, waitingDays, source: 'manual'|'auto' }`.
 *  - metric_hub.review_digest — the Monday summary for a reviewer of the drafts
 *    waiting on a review that they didn't propose. `metadata` carries
 *    `{ count, metrics, models, oldestTitle, oldestDays }`.
 */
export const notificationKindSchema = z.enum([
  'resource.shared_with_you',
  'resource.shared_with_area',
  'resource.created_in_area',
  'announcement.created',
  'resource.click_milestone',
  'metric_hub.draft_submitted',
  'metric_hub.draft_decision',
  'metric_hub.draft_comment',
  'metric_hub.draft_mention',
  'metric_hub.metric_comment',
  'metric_hub.metric_mention',
  'metric_hub.semantic_layer_sync',
  'metric_hub.training_reminder',
  'metric_hub.downstream_changed',
  'metric_hub.consumer_digest',
  'metric_hub.review_reminder',
  'metric_hub.review_digest',
  'metric_hub.board_shared_with_you',
  'metric_hub.board_transferred',
  // metadata: { cellTitle, status, value, target, from, to, transition }
  'metric_hub.board_alert',
  // metadata: { count, cells: [{ title, value, status }], cadence }
  'metric_hub.board_digest',
]);
export type NotificationKind = z.infer<typeof notificationKindSchema>;

export const notificationSchema = z.object({
  id: z.string(),
  /** The recipient. Always lowercased on write so equality checks work. */
  targetEmail: z.string().email(),
  kind: notificationKindSchema,
  /** Resource the notification is about; null for `announcement.created`. */
  resourceId: z.string().nullable().default(null),
  /** Snapshot title at the time of the event — keeps the bell readable
   *  even if the resource is later deleted or renamed. */
  resourceTitle: z.string().nullable().default(null),
  /** URL to open when the user clicks the notification. Always present
   *  except on milestone events (which open the resource page itself). */
  resourceUrl: z.string().nullable().default(null),
  /** Who triggered the event. Null for system-driven kinds (milestones). */
  fromEmail: z.string().email().nullable().default(null),
  fromName: z.string().nullable().default(null),
  /** Optional structured payload — e.g. milestone count, announcement id. */
  metadata: z.record(z.string(), z.unknown()).default({}),
  read: z.boolean().default(false),
  createdAt: z.string().datetime(),
});
export type Notification = z.infer<typeof notificationSchema>;

/**
 * Read-tolerant wrapper. We don't expect legacy GAS notifications to land
 * here — those used a different shape (`userId`, `message`, `read`) and
 * we're explicitly NOT migrating them per Phase 10's "no destructive
 * migration" policy. But we keep a tolerant variant so a hand-edited or
 * partially-written doc still parses with sane defaults.
 */
export const notificationReadSchema = z.preprocess((raw) => {
  if (typeof raw !== 'object' || raw === null) return raw;
  const obj = raw as Record<string, unknown>;
  const out: Record<string, unknown> = { ...obj };
  // Legacy `userId` (email) → targetEmail.
  if (out.targetEmail === undefined && typeof out.userId === 'string') {
    out.targetEmail = out.userId;
  }
  // Some early docs lacked `kind` — fall back to a generic resource-share
  // event so they at least render in the inbox.
  if (typeof out.kind !== 'string') {
    out.kind = 'resource.shared_with_you';
  }
  return out;
}, notificationSchema);

/**
 * Response from `GET /notifications` — items + the count of unread ones.
 * The unread count is a separate field (rather than derived client-side)
 * because the listing is capped at 50 items but the badge should reflect
 * the true total. Server computes both in a single Firestore scan.
 */
export const notificationListResponseSchema = z.object({
  items: z.array(notificationSchema),
  unreadCount: z.number().int().nonnegative(),
});
export type NotificationListResponse = z.infer<typeof notificationListResponseSchema>;
